import { useCombobox } from 'downshift';
import { useState, Fragment, useEffect, useRef } from 'react';

import { CloseOutlineIc, SearchOutlineIc } from '@dsch/dd-icons';

import {
  InputButton,
  InputButtonProps,
} from '../../../../components/Toolkit/InputButton/InputButton';
import { Text } from '../../../../components/ToolkitV2/Text/Text';

import {
  ListItem,
  InputWrapper,
  SearchIcon,
  KeywordSearchInput,
  ClearButton,
  Heading,
  List,
  EmptyList,
  ErrorList,
  ErrorListContent,
  SkeletonListItem,
} from './KeywordSearch.styled';
import { SkeletonBlock } from '../../../../components/ToolkitV2/Skeleton/Skeleton';

import {
  SearchableDropdown,
  highlight,
} from '../../../../components/ToolkitV2/SearchableDropdown/SearchableDropdown';

import type { TextProps } from '../../../../components/ToolkitV2/Text/Text.typed';
import {
  SEARCH_TYPES,
  type MakeModelOption,
  type SearchTypes,
} from '../../MakeModelKeywordSearch.typed';
import { DisplayContainerConfig } from '../../../../components/Support/DisplayContainer/DisplayContainer';
import { MakeModelKeywordSearchModal } from '../MakeModelKeywordSearchModal/MakeModelKeywordSearchModal';
import { theme } from '../../../../helpers/Theme';
import ResponsiveDisplay from '../ResponsiveDisplay/ResponsiveDisplay';
import { pushToDataLayer } from '../../../../services/gtmService';
import { useMakeModelKeywordSearch } from './KeywordSearch.hook';
import { useSlowLoading } from './useSlowLoading';
import Link from 'next/link';
import { ErrorIcon } from '../../../../components/Toolkit/Notification/Notification.styled';
import { LinkButton } from '../../../../components/Toolkit/Button/LinkButton';
import usePopularAndAllMakes from './usePopularAndAllMakes';

type DownshiftKeywordSearchProps = {
  value: string;
  onSelect: (option: MakeModelOption) => void;
  initialItems: Array<MakeModelOption>;
  modalDisplayConfig: DisplayContainerConfig;
  dropdownDisplayConfig: DisplayContainerConfig;
  page: 'hp' | null;
  minPrice?: string;
  maxPrice?: string;
  minYear?: string;
  maxYear?: string;
};

/**
 * This component is responsible for displaying the UI and managing its logic in an accessible manner.
 *
 * It contains a modal and dropdown. Each of these can be configured to be displayed or hidden on different screen sizes.
 * They both contain list items that display makes or make and model options.
 *
 * useCombobox from downshift is used to managed the internal state of the dropdown and list items for both and the modal and the dropdown.
 * As a result, the input of the component is consistent across screen sizes.
 *
 * The state of the modal is managed separately. React modal requires its own state for displaying the modal with the list and dismissing it.
 */
export const KeywordSearch = (props: DownshiftKeywordSearchProps) => {
  const {
    value,
    onSelect,
    initialItems,
    modalDisplayConfig,
    dropdownDisplayConfig,
    page,
    minPrice,
    maxPrice,
    minYear,
    maxYear,
  } = props;

  const analyticsSearchTypeRef = useRef<SearchTypes>();

  const [inputValue, setInputValue] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);

  const {
    data: makeModelOptions,
    isPending,
    isError,
    reload,
  } = useMakeModelKeywordSearch({
    minPrice,
    maxPrice,
    minYear,
    maxYear,
    keyword: inputValue,
    listIsOpen: isOpen,
  });

  const { data: popularAndAllMakes } = usePopularAndAllMakes({
    minPrice,
    maxPrice,
    minYear,
    maxYear,
    initialData: initialItems,
  });
  const loadingIsSlow = useSlowLoading(isPending, 500);

  // We need the items to be in a state because we want to display the last items
  // while the new items are being fetched
  const [items, setItems] = useState<MakeModelOption[] | undefined>(undefined);
  useEffect(() => {
    if (inputValue === '') {
      setItems(popularAndAllMakes);
    } else if (makeModelOptions !== undefined) {
      setItems(makeModelOptions);
    }
  }, [makeModelOptions, popularAndAllMakes, inputValue]);

  /**
   * A 1-based index is used for more inititive analytics data
   */
  const findPositionOfSelectedItem = (selectedItem: MakeModelOption) => {
    if (!items) return null;

    const index = items.findIndex(({ value }) => value === selectedItem.value);
    return index === -1 ? null : index + 1;
  };

  // Reset scroll position when items change
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [items]);

  const {
    getInputProps,
    getMenuProps,
    getToggleButtonProps,
    getItemProps,
    highlightedIndex,
    openMenu,
    closeMenu,
  } = useCombobox({
    items: items ?? [],
    itemToString: (item) => item?.label ?? '',
    initialInputValue: value,
    onSelectedItemChange: ({ selectedItem }) => {
      onSelect(selectedItem);
      /**
       * We update the search type to be "list" if the user has not interacted with the input by typing.
       *
       * The input value may change when the user selects an item from the list,
       * but we distinguish this type of change from changes made by the user through direct interactions with the input.
       */
      if (analyticsSearchTypeRef.current !== SEARCH_TYPES.keyboard) {
        analyticsSearchTypeRef.current = SEARCH_TYPES.list;
      }
    },
    inputValue,
    onInputValueChange: ({ inputValue }) => setInputValue(inputValue),
    stateReducer(_state, actionAndChanges) {
      const { type, changes } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputClick:
          // Without this, the dropdown would close when the searchbox is selected.
          return {
            ...changes,
            isOpen: true,
          };
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          if (changes.selectedItem?.value === null) {
            return {
              ...changes,
            };
          }

          return {
            ...changes,
            // Don't close the dropdown when we select an item from the initial list of makes
            isOpen: items === popularAndAllMakes,
            // Avoid having (All Models) in the input when an item is selected
            inputValue: `${changes.selectedItem?.value?.make} ${
              changes.selectedItem?.value?.model ?? ''
            }`.trim(),
          };
        default:
          return changes;
      }
    },
    isOpen,
    onIsOpenChange: ({ isOpen, selectedItem }) => {
      setIsOpen(isOpen);
      if (isOpen) {
        setInputValue(value);
        if (page) {
          pushToDataLayer({
            event: `${page}-make-model-click`,
          });
        }
      } else if (analyticsSearchTypeRef.current && page) {
        pushToDataLayer({
          event: `${page}-make-model-selected`,
          searchTerm: selectedItem?.label,
          searchType: analyticsSearchTypeRef.current,
          autocompletePosition: selectedItem
            ? findPositionOfSelectedItem(selectedItem)
            : null,
        });
        analyticsSearchTypeRef.current = undefined;
      }
    },
  });

  const TEXT_PROPS: TextProps = {
    size: 'M16',
    forwardedAs: 'span',
  };

  const KEYWORD_SEARCH_INPUT_PROPS = {
    type: 'search',
    placeholder: 'Start typing a make or model',
    'aria-label': 'Search',
  };

  const INPUT_BUTTON_PROPS: InputButtonProps = {
    placeholder: 'All makes/models',
    isActive: false,
    heightVariant: 'LARGE',
  };

  const clearSelection = (
    e:
      | React.MouseEvent<HTMLButtonElement>
      | React.TouchEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    setInputValue('');
    onSelect({ label: '', value: null, count: null });
    analyticsSearchTypeRef.current = undefined;
  };

  const handleOpenModal = () => {
    // iOS keyboard workaround: imperatively make modal content visible before focusing.
    // This must happen synchronously during the user interaction for iOS to show the keyboard.
    if (modalContentRef.current) {
      modalContentRef.current.style.visibility = 'visible';
    }
    // Focus the input inside the modal and display the soft keyboard
    inputRef.current?.focus();
    openMenu();
  };

  const renderListItems = () => {
    if (isError && !isPending) {
      return (
        <ErrorList>
          <ErrorListContent>
            <ErrorIcon
              style={{ marginBottom: 'auto' }}
              color={theme.donedeal.colors.GREY}
              width={40}
              height={40}
            />
            <Text size="L18" color="GREY_DARKER">
              <strong>Something went wrong</strong>
            </Text>
            <Text {...TEXT_PROPS} align="center" color="GREY_DARK">
              There was a problem processing your request -- please click{' '}
              <LinkButton inline onClick={reload}>
                reload
              </LinkButton>{' '}
              to try again.
            </Text>
          </ErrorListContent>
        </ErrorList>
      );
    }

    if (loadingIsSlow) {
      return (
        <div data-testid="keyword-search-skeleton-list">
          {Array.from({ length: 15 }).map((_, index) => (
            <SkeletonListItem key={index}>
              <SkeletonBlock w="100%" h="100%" />
            </SkeletonListItem>
          ))}
        </div>
      );
    }

    if (items === undefined) {
      return;
    }

    if (items.length === 0) {
      return (
        <EmptyList>
          <div>
            <Text {...TEXT_PROPS} color="GREY_DARKER">
              We couldn't find matches for "<strong>{inputValue}</strong>"
            </Text>
          </div>
          <div>
            <Link href="/cars">View all Makes & Models</Link>
          </div>
        </EmptyList>
      );
    }

    return items.map((item, index) => {
      const { label, count, groupLabel } = item;
      return (
        <Fragment key={`${label}-${index}`}>
          {groupLabel && (
            <Heading weight="bold" color="GREY_DARKER" size="M16">
              {groupLabel}
            </Heading>
          )}
          <ListItem
            {...getItemProps({
              item,
              index,
            })}
            highlighted={highlightedIndex === index}
          >
            {
              <Text {...TEXT_PROPS} color="GREY_DARKER">
                {highlight({
                  item: label,
                  searchText: inputValue,
                })}
              </Text>
            }
            {count ? (
              <Text {...TEXT_PROPS} color="GREY_DARK">
                {' '}
                ({count})
              </Text>
            ) : (
              ''
            )}
          </ListItem>
        </Fragment>
      );
    });
  };

  return (
    <>
      {/* Modal
        NB: We're using ResponsiveDisplay instead of DisplayContainer to make sure
        we don't have the two components mounted on the DOM at the same time.
        Indeed if that is the case we would have problems with refs because
        we're using the same downshift props for both. */}
      <ResponsiveDisplay
        small={modalDisplayConfig.small === 'block'}
        medium={modalDisplayConfig.medium === 'block'}
        large={modalDisplayConfig.large === 'block'}
      >
        <InputButton
          ref={triggerButtonRef}
          placeholderColor={theme.donedeal.colors.GREY}
          onClick={handleOpenModal}
          value={value}
          aria-label="Open make & model modal"
          {...INPUT_BUTTON_PROPS}
        />
        <MakeModelKeywordSearchModal
          isOpen={isOpen}
          onRequestClose={closeMenu}
          contentRef={modalContentRef}
          returnFocusTo={triggerButtonRef}
        >
          <InputWrapper margin={`0 ${theme.donedeal.spacing.M16}`}>
            <SearchIcon aria-hidden="true">
              <SearchOutlineIc />
            </SearchIcon>
            <KeywordSearchInput
              {...getInputProps({
                onChange: () => {
                  analyticsSearchTypeRef.current = SEARCH_TYPES.keyboard;
                },
                ref: inputRef,
                ...KEYWORD_SEARCH_INPUT_PROPS,
              })}
            />
            {inputValue && (
              <ClearButton
                // Use onMouseDown or onTouchStart to prevent the dropdown from closing
                // Indeed, preventDefault prevents the input from losing focus
                onMouseDown={clearSelection}
                onTouchStart={clearSelection}
                aria-label="Clear search"
                ofType="GHOST"
              >
                <CloseOutlineIc aria-hidden="true" width={16} height={16} />
              </ClearButton>
            )}
          </InputWrapper>
          <List
            {...getMenuProps({ ref: listRef })}
            aria-label="Make and model options"
          >
            {isOpen && renderListItems()}
          </List>
        </MakeModelKeywordSearchModal>
      </ResponsiveDisplay>

      {/* Dropdown */}
      <ResponsiveDisplay
        small={dropdownDisplayConfig.small === 'block'}
        medium={dropdownDisplayConfig.medium === 'block'}
        large={dropdownDisplayConfig.large === 'block'}
      >
        {!isOpen && (
          <InputButton
            placeholderColor={theme.donedeal.colors.GREY}
            value={value}
            aria-label="Toggle make & model dropdown"
            {...INPUT_BUTTON_PROPS}
            {...getToggleButtonProps({ tabIndex: 0 })}
          />
        )}
        <SearchableDropdown isOpen={isOpen}>
          <InputWrapper>
            <SearchIcon aria-hidden="true">
              <SearchOutlineIc />
            </SearchIcon>
            <KeywordSearchInput
              {...getInputProps({
                onChange: () => {
                  analyticsSearchTypeRef.current = SEARCH_TYPES.keyboard;
                },
                ...KEYWORD_SEARCH_INPUT_PROPS,
              })}
            />
            {inputValue && (
              <ClearButton
                // Use onMouseDown or onTouchStart to prevent the dropdown from closing
                // Indeed, preventDefault prevents the input from losing focus
                onMouseDown={clearSelection}
                onTouchStart={clearSelection}
                aria-label="Clear search"
                ofType="GHOST"
              >
                <CloseOutlineIc aria-hidden="true" width={16} height={16} />
              </ClearButton>
            )}
          </InputWrapper>
          <List
            {...getMenuProps({ ref: listRef })}
            variant="dropdown"
            aria-label="Make and model options"
          >
            {isOpen && renderListItems()}
          </List>
        </SearchableDropdown>
      </ResponsiveDisplay>
    </>
  );
};
